import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Save,
  User,
  MapPin,
  ShieldCheck,
  Wand2,
  Loader2,
  Building2,
  CreditCard,
  Clock,
  Briefcase,
  Phone,
  AlertCircle,
  Wallet,
  CheckCircle2,
  IndianRupee,
  Percent,
  Calendar,
  Mail,
  Hash,
  Lock,
  UserCog,
  X,
  Plus,
  FileText,
  Upload,
  Trash2,
  Eye,
  CheckCircle,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useEmployee from "@/hooks/useEmployee";
import employeeService from "@/services/employeeService";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
const TABS = [
  { id: "basic", label: "Basic Info", icon: User },
  { id: "employment", label: "Employment Details", icon: Briefcase },
  { id: "address", label: "Address & Emergency", icon: MapPin },
  { id: "auth", label: "Auth & Access", icon: ShieldCheck },
  { id: "bank", label: "Bank Details", icon: CreditCard },
  // { id: "documents", label: "Documents", icon: FileText }, // Hidden for now
  { id: "payroll", label: "Payroll Info", icon: Wallet },
  { id: "shift", label: "Shift & Stations", icon: Clock },
];

// Test data generator
const generateTestData = () => {
  const firstNames = ["John", "Jane", "Alex", "Sarah", "Mike", "Emily", "David", "Lisa"];
  const lastNames = ["Smith", "Johnson", "Williams", "Brown", "Jones", "Miller", "Davis"];
  const cities = ["Mumbai", "Delhi", "Bangalore", "Chennai", "Kolkata", "Hyderabad"];
  const states = ["Maharashtra", "Delhi", "Karnataka", "Tamil Nadu", "West Bengal"];
  const designations = ["Chef", "Waiter", "Manager", "Cashier", "Host", "Bartender"];

  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const randomNum = Math.floor(Math.random() * 9000) + 1000;
  const randomPhone = `98${Math.floor(Math.random() * 90000000) + 10000000}`;

  const today = new Date();
  const birthYear = today.getFullYear() - Math.floor(Math.random() * 20) - 22;
  const dob = `${birthYear}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`;
  const joinYear = today.getFullYear() - Math.floor(Math.random() * 3);
  const doj = `${joinYear}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`;

  return {
    username: `${firstName.toLowerCase()}${randomNum}`,
    password: "Test@123",
    userType: "MANAGEMENT",
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${randomNum}@test.com`,
    phoneNumber: randomPhone,
    secondaryPhone: `97${Math.floor(Math.random() * 90000000) + 10000000}`,
    gender: Math.random() > 0.5 ? "MALE" : "FEMALE",
    dateOfBirth: dob,
    dateOfJoining: doj,
    designation: designations[Math.floor(Math.random() * designations.length)],
    employeeCode: `EMP${randomNum}`,
    employmentType: "FULL_TIME",
    employmentStatus: "ACTIVE",
    totalExperience: `${Math.floor(Math.random() * 10) + 1} years`,
    address1: `${Math.floor(Math.random() * 999) + 1}, Test Street`,
    address2: `Near Test Landmark, Block ${String.fromCharCode(65 + Math.floor(Math.random() * 10))}`,
    city: cities[Math.floor(Math.random() * cities.length)],
    state: states[Math.floor(Math.random() * states.length)],
    country: "India",
    zipcode: String(Math.floor(Math.random() * 900000) + 100000),
    emergencyContactName: `${lastNames[Math.floor(Math.random() * lastNames.length)]} ${firstName}`,
    emergencyContactPhone: `98${Math.floor(Math.random() * 90000000) + 10000000}`,
    emergencyContactRelation: ["Father", "Mother", "Spouse", "Sibling"][Math.floor(Math.random() * 4)],
    pinCode: String(Math.floor(Math.random() * 9000) + 1000),
    canAccessPos: true,
    maxDiscountPercent: Math.floor(Math.random() * 20) + 5,
    canVoidOrders: Math.random() > 0.5,
    canRefund: Math.random() > 0.5,
    canOpenCashDrawer: Math.random() > 0.5,
    canApplyDiscount: true,
    roleId: "",
    addressId: null,
  };
};

export default function EmployeeAddEdit() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canAddEmployee = hasPermission(P.HRM.EMPLOYEES.ADD);
  const canEditEmployee = hasPermission(P.HRM.EMPLOYEES.EDIT);
  const canViewEmployee = hasPermission(P.HRM.EMPLOYEES.SCREEN);
  const { id } = useParams();
  const location = useLocation();

  const isEdit = location.pathname.includes("edit");
  const isView = location.pathname.includes("view");
  const isAdd = !isEdit && !isView;

  const {
    fetchEmployeeById,
    createEmployee,
    updateEmployee,
    fetchRoles,
    roles,
    rolesLoading,
  } = useEmployee();

  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(isEdit || isView);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [refData, setRefData] = useState(null);
  const [selectedStationId, setSelectedStationId] = useState("");
  const [addingStation, setAddingStation] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [savingDocument, setSavingDocument] = useState(false);
  const [newDocument, setNewDocument] = useState({
    documentTypeId: "",
    documentNumber: "",
    documentName: "",
    issueDate: "",
    expiryDate: "",
    remarks: "",
  });

  const [form, setForm] = useState({
    // Basic Info
    username: "",
    password: "",
    userType: "MANAGEMENT",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    secondaryPhone: "",
    gender: "MALE",
    dateOfBirth: "",

    // Employment Info
    employeeCode: "",
    designation: "",
    employmentType: "FULL_TIME",
    employmentStatus: "ACTIVE",
    dateOfJoining: "",
    totalExperience: "",

    // Address
    address1: "",
    address2: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
    addressId: null,

    // Emergency Contact
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelation: "",

    // Auth & Access
    roleId: "",
    pinCode: "",
    canAccessPos: true,
    maxDiscountPercent: "",
    canVoidOrders: false,
    canRefund: false,
    canOpenCashDrawer: false,
    canApplyDiscount: false,

    // Bank Details
    bankDetails: [],

    // Payroll Info
    payrollInfo: {
      basicSalary: "",
      hourlyRate: "",
      dailyRate: "",
      salaryType: "MONTHLY",
      payFrequency: "MONTHLY",
      hraAllowance: "",
      transportAllowance: "",
      mealAllowance: "",
      otherAllowances: "",
      pfContribution: "",
      esiContribution: "",
      professionalTax: "",
      tdsPercent: "",
      pfAccountNumber: "",
      esiNumber: "",
      uanNumber: "",
      isOvertimeEligible: false,
      overtimeRate: "",
      effectiveFrom: "",
    },

    // Shift Preferences
    shiftPreferences: {
      preferredShift: "",
      maxHoursPerDay: "",
      maxHoursPerWeek: "",
      availableMonday: true,
      availableTuesday: true,
      availableWednesday: true,
      availableThursday: true,
      availableFriday: true,
      availableSaturday: true,
      availableSunday: false,
    },

    // Assigned Stations
    assignedStations: [],
  });

  // Auto-set userType to SUPER_USER if role is Owner
  useEffect(() => {
    //debugger
    if (roles && form.roleId) {
      const selectedRole = roles.find(r => r.id === form.roleId);
      if (selectedRole && selectedRole.name.toLowerCase() === 'owner') {
        setForm(prev => ({ ...prev, userType: 'SUPER_USER' }));
      } else {
        setForm(prev => ({ ...prev, userType: 'MANAGEMENT' }));
      }
    }
  }, [form.roleId]);

  useEffect(() => {
    fetchRoles();
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const data = await employeeService.getReferenceData();
      setRefData(data);
      if (data?.documentTypes) {
        setDocumentTypes(data.documentTypes);
      }
    } catch (err) {
      console.error("Failed to load reference data:", err);
    }
  };

  useEffect(() => {
    if (!isEdit && !isView) return;

    const load = async () => {
      try {
        const u = await fetchEmployeeById(id);
        const addr = u.address?.[0] || {};

        setForm(prev => ({
          ...prev,
          username: u.username || "",
          password: "",
          userType: u.userType || "MANAGEMENT",
          firstName: u.firstName || "",
          lastName: u.lastName || "",
          email: u.email || "",
          phoneNumber: u.phoneNumber || "",
          secondaryPhone: u.secondaryPhone || "",
          gender: u.gender || "MALE",
          dateOfBirth: u.dateOfBirth ? u.dateOfBirth.split("T")[0] : "",
          employeeCode: u.employeeCode || "",
          designation: u.designation || "",
          employmentType: u.employmentType || "FULL_TIME",
          employmentStatus: u.employmentStatus || "ACTIVE",
          dateOfJoining: u.dateOfJoining ? u.dateOfJoining.split("T")[0] : "",
          totalExperience: u.totalExperience || "",
          address1: addr.address1 || "",
          address2: addr.address2 || "",
          city: addr.city || "",
          state: addr.state || "",
          country: addr.country || "",
          zipcode: addr.zipcode || "",
          addressId: addr.id || null,
          emergencyContactName: u.emergencyContactName || "",
          emergencyContactPhone: u.emergencyPhoneNumber || "",
          emergencyContactRelation: u.emergencyContactRelation || "",
          roleId: u.roles?.[0]?.id || "",
          pinCode: u.pinCode || "",
          canAccessPos: u.canAccessPos ?? true,
          maxDiscountPercent: u.maxDiscountPercent || "",
          canVoidOrders: u.canVoidOrders ?? false,
          canRefund: u.canRefund ?? false,
          canOpenCashDrawer: u.canOpenCashDrawer ?? false,
          canApplyDiscount: u.canApplyDiscount ?? false,
        }));

        // Load additional employee details
        if (isEdit || isView) {
          try {
            const [bankData, shiftData, stationData, payrollData, docsData] = await Promise.all([
              employeeService.getBankDetails(id).catch(() => []),
              employeeService.getShiftPreferences(id).catch(() => null),
              employeeService.getAssignedStations(id).catch(() => []),
              employeeService.getPayrollInfo(id).catch(() => null),
              employeeService.getDocuments(id).catch(() => []),
            ]);

            setForm(prev => ({
              ...prev,
              bankDetails: bankData || [],
              shiftPreferences: shiftData || prev.shiftPreferences,
              assignedStations: stationData || [],
              payrollInfo: payrollData ? {
                ...prev.payrollInfo,
                ...payrollData,
                effectiveFrom: payrollData.effectiveFrom ? payrollData.effectiveFrom.split("T")[0] : "",
              } : prev.payrollInfo,
            }));
            setDocuments(docsData || []);
          } catch (err) {
            console.error("Failed to load extended employee data:", err);
          }
        }
      } catch (err) {
        console.error("Failed to load employee:", err);
      }
      setLoading(false);
    };

    load();
  }, [id, isEdit, isView]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const updateShiftField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      shiftPreferences: { ...prev.shiftPreferences, [key]: value },
    }));
  };

  const updateBankField = (key, value) => {
    setForm((prev) => {
      const currentBank = prev.bankDetails?.[0] || { isPrimary: true };
      return {
        ...prev,
        bankDetails: [{ ...currentBank, [key]: value }],
      };
    });
  };

  const updatePayrollField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      payrollInfo: { ...prev.payrollInfo, [key]: value },
    }));
  };

  // Document handlers
  const handleSaveDocument = async () => {
    if (!newDocument.documentTypeId || !id) return;

    setSavingDocument(true);
    try {
      const saved = await employeeService.saveDocument(id, {
        ...newDocument,
        issueDate: newDocument.issueDate || null,
        expiryDate: newDocument.expiryDate || null,
      });
      setDocuments(prev => [...prev, saved]);
      setNewDocument({
        documentTypeId: "",
        documentNumber: "",
        documentName: "",
        issueDate: "",
        expiryDate: "",
        remarks: "",
      });
    } catch (err) {
      console.error("Failed to save document:", err);
    }
    setSavingDocument(false);
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      await employeeService.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const handleVerifyDocument = async (docId) => {
    try {
      const verified = await employeeService.verifyDocument(docId);
      setDocuments(prev => prev.map(d => d.id === docId ? verified : d));
    } catch (err) {
      console.error("Failed to verify document:", err);
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    if (!form.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
    if (!form.dateOfBirth.trim()) newErrors.dateOfBirth = "Date of birth is required";
    if (!form.username.trim()) newErrors.username = "Username is required";
    if (isAdd && !form.password.trim()) newErrors.password = "Password is required";
    if (!form.roleId) newErrors.roleId = "Role is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getBackPath = () => {
    return location.pathname.startsWith('/hrm') ? '/hrm/employees' : '/admin/employee';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView || !validateForm()) return;

    setSaving(true);
    const formatDate = (date) => (date ? `${date}T00:00:00` : null);

    const payload = {
      id: isEdit ? id : undefined,
      username: form.username,
      password: form.password || undefined,
      userType: form.userType,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      phoneNumber: form.phoneNumber,
      secondaryPhone: form.secondaryPhone || undefined,
      dateOfBirth: formatDate(form.dateOfBirth),
      dateOfJoining: formatDate(form.dateOfJoining),
      employeeCode: form.employeeCode || undefined,
      designation: form.designation || undefined,
      employmentType: form.employmentType || undefined,
      employmentStatus: form.employmentStatus || undefined,
      totalExperience: form.totalExperience || undefined,
      organizationId: null,
      branchId: [],
      gender: form.gender,
      address: [
        {
          id: isEdit ? form.addressId : null,
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          state: form.state,
          country: form.country,
          zipcode: form.zipcode,
        },
      ],
      emergencyContactName: form.emergencyContactName || undefined,
      emergencyPhoneNumber: form.emergencyContactPhone || undefined,
      emergencyContactRelation: form.emergencyContactRelation || undefined,
      roles: [{ id: form.roleId }],
      pinCode: form.pinCode || undefined,
      canAccessPos: form.canAccessPos,
      maxDiscountPercent: form.maxDiscountPercent ? parseFloat(form.maxDiscountPercent) : undefined,
      canVoidOrders: form.canVoidOrders,
      canRefund: form.canRefund,
      canOpenCashDrawer: form.canOpenCashDrawer,
      canApplyDiscount: form.canApplyDiscount,
      active: true,
    };

    try {
      let savedUser;
      if (isEdit) {
        savedUser = await updateEmployee(id, payload);
      } else {
        savedUser = await createEmployee(payload);
      }

      const userId = isEdit ? id : savedUser?.id;

      // Save bank details
      if (userId && form.bankDetails?.[0]?.bankName) {
        try {
          await employeeService.saveBankDetails(userId, form.bankDetails[0]);
        } catch (err) {
          console.error("Failed to save bank details:", err);
        }
      }

      // Save payroll info
      if (userId && form.payrollInfo?.basicSalary) {
        try {
          const payrollPayload = {
            ...form.payrollInfo,
            basicSalary: parseFloat(form.payrollInfo.basicSalary) || 0,
            hourlyRate: form.payrollInfo.hourlyRate ? parseFloat(form.payrollInfo.hourlyRate) : null,
            dailyRate: form.payrollInfo.dailyRate ? parseFloat(form.payrollInfo.dailyRate) : null,
            hraAllowance: form.payrollInfo.hraAllowance ? parseFloat(form.payrollInfo.hraAllowance) : null,
            transportAllowance: form.payrollInfo.transportAllowance ? parseFloat(form.payrollInfo.transportAllowance) : null,
            mealAllowance: form.payrollInfo.mealAllowance ? parseFloat(form.payrollInfo.mealAllowance) : null,
            otherAllowances: form.payrollInfo.otherAllowances ? parseFloat(form.payrollInfo.otherAllowances) : null,
            pfContribution: form.payrollInfo.pfContribution ? parseFloat(form.payrollInfo.pfContribution) : null,
            esiContribution: form.payrollInfo.esiContribution ? parseFloat(form.payrollInfo.esiContribution) : null,
            professionalTax: form.payrollInfo.professionalTax ? parseFloat(form.payrollInfo.professionalTax) : null,
            tdsPercent: form.payrollInfo.tdsPercent ? parseFloat(form.payrollInfo.tdsPercent) : null,
            overtimeRate: form.payrollInfo.overtimeRate ? parseFloat(form.payrollInfo.overtimeRate) : null,
            effectiveFrom: form.payrollInfo.effectiveFrom ? `${form.payrollInfo.effectiveFrom}T00:00:00` : null,
          };
          await employeeService.savePayrollInfo(userId, payrollPayload);
        } catch (err) {
          console.error("Failed to save payroll info:", err);
        }
      }

      // Save shift preferences
      if (userId && (form.shiftPreferences?.preferredShift || form.shiftPreferences?.maxHoursPerWeek)) {
        try {
          await employeeService.saveShiftPreferences(userId, form.shiftPreferences);
        } catch (err) {
          console.error("Failed to save shift preferences:", err);
        }
      }

      navigate(getBackPath());
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleFillTestData = () => {
    setForm(prev => ({ ...prev, ...generateTestData() }));
    setErrors({});
  };
  if (isAdd && !canAddEmployee) {
  return (
    <div className="p-6 text-center">
      <p className="text-red-500 font-semibold">
        You do not have permission to create employees
      </p>
    </div>
  );
}

if (isEdit && !canEditEmployee) {
  return (
    <div className="p-6 text-center">
      <p className="text-red-500 font-semibold">
        You do not have permission to edit employees
      </p>
    </div>
  );
}

if (!canViewEmployee) {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>

        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
          <p className="mt-3 text-slate-600 font-medium">Loading employee data...</p>
        </div>
      </div>
    );
  }

  const currentTab = TABS.find(t => t.id === activeTab);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 px-4 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(getBackPath())}
              className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all duration-200"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900">
                  {isView ? "Employee Details" : isEdit ? "Edit Employee" : "New Employee"}
                </h1>
                {isEdit && form.firstName && (
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full">
                    {form.firstName} {form.lastName}
                  </span>
                )}
                {isEdit && form.employeeCode && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-mono rounded">
                    {form.employeeCode}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {isView ? "View employee information" : isEdit ? `Updating profile for ${form.firstName || 'employee'}` : "Add a new team member to your organization"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isAdd && (
              <button
                type="button"
                onClick={handleFillTestData}
                className="px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200 rounded-xl hover:from-amber-100 hover:to-orange-100 flex items-center gap-2 text-sm font-medium transition-all duration-200"
              >
                <Wand2 size={16} />
                Auto Fill
              </button>
            )}
            {!isView && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(getBackPath())}
                  className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium transition-all duration-200"
                >
                  Cancel
                </button>
            {((isAdd && canAddEmployee) || (isEdit && canEditEmployee)) && (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 flex items-center gap-2 text-sm font-semibold disabled:opacity-50 transition-all duration-200"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update Employee" : "Create Employee"}
                </>
              )}
            </button>
            )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation - Classic Style */}
      <div className="bg-white border-b border-slate-200 sticky top-[73px] z-10">
        <div className="flex">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 ${isActive
                  ? "border-teal-600 text-teal-700 bg-teal-50/50"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-4 py-4">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-6">
                <FormCard
                  icon={User}
                  title="Personal Information"
                  subtitle="Basic details about the employee"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <FormInput
                      icon={User} label="First Name" required error={errors.firstName} disabled={isView}
                      value={form.firstName} onChange={(e) => updateField("firstName", e.target.value)}
                      placeholder="Enter first name"
                    />
                    <FormInput
                      icon={User} label="Last Name" disabled={isView}
                      value={form.lastName} onChange={(e) => updateField("lastName", e.target.value)}
                      placeholder="Enter last name"
                    />
                    <FormInput
                      icon={Mail} label="Email" type="email" required error={errors.email} disabled={isView}
                      value={form.email} onChange={(e) => updateField("email", e.target.value)}
                      placeholder="email@example.com"
                    />
                    <FormInput
                      icon={Phone} label="Phone Number" required error={errors.phoneNumber} disabled={isView}
                      value={form.phoneNumber} onChange={(e) => updateField("phoneNumber", e.target.value)}
                      placeholder="10-digit number"
                    />
                    <FormInput
                      icon={Phone} label="Secondary Phone" disabled={isView}
                      value={form.secondaryPhone} onChange={(e) => updateField("secondaryPhone", e.target.value)}
                      placeholder="Alternate number"
                    />
                    <FormSelect
                      icon={User} label="Gender" disabled={isView}
                      value={form.gender} onChange={(e) => updateField("gender", e.target.value)}
                      options={[
                        { label: "Male", value: "MALE" },
                        { label: "Female", value: "FEMALE" },
                        { label: "Other", value: "OTHER" }
                      ]}
                    />
                    <FormInput
                      icon={Calendar} label="Date of Birth" type="date" required error={errors.dateOfBirth} disabled={isView}
                      value={form.dateOfBirth} onChange={(e) => updateField("dateOfBirth", e.target.value)}
                    />
                    <FormInput
                      icon={Hash} label="Employee Code" disabled={isView}
                      value={form.employeeCode} onChange={(e) => updateField("employeeCode", e.target.value)}
                      placeholder="EMP001"
                    />
                  </div>
                </FormCard>
              </div>
            )}

            {/* Employment Tab */}
            {activeTab === "employment" && (
              <FormCard
                icon={Briefcase}
                title="Employment Details"
                subtitle="Job role and employment information"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <FormInput
                    icon={Briefcase} label="Designation" disabled={isView}
                    value={form.designation}
                    onChange={(e) => updateField("designation", e.target.value)}
                    placeholder="e.g., Head Chef"
                  />
                  <FormSelect
                    icon={Briefcase} label="Employment Type" disabled={isView}
                    value={form.employmentType}
                    onChange={(e) => updateField("employmentType", e.target.value)}
                    options={refData?.employmentTypes || [
                      { label: "Full Time", value: "FULL_TIME" },
                      { label: "Part Time", value: "PART_TIME" },
                      { label: "Contract", value: "CONTRACT" },
                      { label: "Temporary", value: "TEMPORARY" },
                      { label: "Intern", value: "INTERN" },
                    ]}
                  />
                  <FormSelect
                    icon={CheckCircle2} label="Employment Status" disabled={isView}
                    value={form.employmentStatus}
                    onChange={(e) => updateField("employmentStatus", e.target.value)}
                    options={refData?.employmentStatuses || [
                      { label: "Active", value: "ACTIVE" },
                      { label: "Probation", value: "PROBATION" },
                      { label: "Notice Period", value: "NOTICE_PERIOD" },
                      { label: "On Leave", value: "ON_LEAVE" },
                    ]}
                  />
                  <FormInput
                    icon={Calendar} label="Date of Joining" type="date" disabled={isView}
                    value={form.dateOfJoining}
                    onChange={(e) => updateField("dateOfJoining", e.target.value)}
                  />
                  <FormInput
                    icon={Clock} label="Total Experience" disabled={isView}
                    value={form.totalExperience}
                    onChange={(e) => updateField("totalExperience", e.target.value)}
                    placeholder="e.g., 3 years"
                  />
                </div>
              </FormCard>
            )}

            {/* Address & Emergency Tab */}
            {activeTab === "address" && (
              <div className="space-y-6">
                <FormCard
                  icon={MapPin}
                  title="Address Information"
                  subtitle="Current residential address"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <FormInput
                      icon={MapPin} label="Address Line 1" disabled={isView}
                      value={form.address1}
                      onChange={(e) => updateField("address1", e.target.value)}
                      placeholder="Street address"
                    />
                    <FormInput
                      label="Address Line 2" disabled={isView}
                      value={form.address2}
                      onChange={(e) => updateField("address2", e.target.value)}
                      placeholder="Apt, suite, etc."
                    />
                    <FormInput
                      label="City" disabled={isView}
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      placeholder="City"
                    />
                    <FormInput
                      label="State" disabled={isView}
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                      placeholder="State"
                    />
                    <FormInput
                      label="Country" disabled={isView}
                      value={form.country}
                      onChange={(e) => updateField("country", e.target.value)}
                      placeholder="Country"
                    />
                    <FormInput
                      label="Zip Code" disabled={isView}
                      value={form.zipcode}
                      onChange={(e) => updateField("zipcode", e.target.value)}
                      placeholder="Postal code"
                    />
                  </div>
                </FormCard>

                <FormCard
                  icon={Phone}
                  title="Emergency Contact"
                  subtitle="Person to contact in case of emergency"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <FormInput
                      icon={User} label="Contact Name" disabled={isView}
                      value={form.emergencyContactName}
                      onChange={(e) => updateField("emergencyContactName", e.target.value)}
                      placeholder="Full name"
                    />
                    <FormInput
                      icon={Phone} label="Contact Phone" disabled={isView}
                      value={form.emergencyContactPhone}
                      onChange={(e) => updateField("emergencyContactPhone", e.target.value)}
                      placeholder="Phone number"
                    />
                    <FormSelect
                      label="Relationship" disabled={isView}
                      value={form.emergencyContactRelation}
                      onChange={(e) => updateField("emergencyContactRelation", e.target.value)}
                      options={[
                        { label: "Select relationship", value: "" },
                        { label: "Father", value: "Father" },
                        { label: "Mother", value: "Mother" },
                        { label: "Spouse", value: "Spouse" },
                        { label: "Sibling", value: "Sibling" },
                        { label: "Friend", value: "Friend" },
                        { label: "Other", value: "Other" },
                      ]}
                    />
                  </div>
                </FormCard>
              </div>
            )}

            {/* Auth & Access Tab */}
            {activeTab === "auth" && (
              <div className="space-y-6">
                <FormCard
                  icon={Lock}
                  title="Login Credentials"
                  subtitle="Authentication details for system access"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <FormInput
                      icon={User} label="Username" required error={errors.username} disabled={isView}
                      value={form.username}
                      onChange={(e) => updateField("username", e.target.value)}
                      placeholder="Login username"
                    />
                    {!isView && (
                      <FormInput
                        icon={Lock} label="Password" type="password" required={isAdd} error={errors.password}
                        value={form.password}
                        onChange={(e) => updateField("password", e.target.value)}
                        placeholder={isEdit ? "Leave blank to keep" : "Set password"}
                      />
                    )}
                    <FormSelect
                      icon={UserCog} label="Role" required error={errors.roleId} disabled={isView || rolesLoading}
                      value={form.roleId}
                      onChange={(e) => updateField("roleId", e.target.value)}
                      options={rolesLoading
                        ? [{ label: "Loading...", value: "" }]
                        : [{ label: "Select a role", value: "" }, ...roles.map((r) => ({ label: r.name, value: r.id }))]
                      }
                    />
                  </div>
                </FormCard>

                <FormCard
                  icon={ShieldCheck}
                  title="POS Access Control"
                  subtitle="Define what actions this employee can perform on POS"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <FormInput
                      icon={Hash} label="PIN Code" disabled={isView}
                      value={form.pinCode}
                      onChange={(e) => updateField("pinCode", e.target.value)}
                      placeholder="4-6 digit PIN"
                      maxLength={6}
                    />
                    <FormInput
                      icon={Percent} label="Max Discount %" type="number" disabled={isView}
                      value={form.maxDiscountPercent}
                      onChange={(e) => updateField("maxDiscountPercent", e.target.value)}
                      placeholder="e.g., 10"
                      min={0}
                      max={100}
                    />
                  </div>
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <label className="block text-sm font-medium text-slate-700 mb-3">Permissions</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      <ToggleSwitch
                        label="Access POS"
                        checked={form.canAccessPos}
                        disabled={isView}
                        onChange={(checked) => updateField("canAccessPos", checked)}
                      />
                      <ToggleSwitch
                        label="Void Orders"
                        checked={form.canVoidOrders}
                        disabled={isView}
                        onChange={(checked) => updateField("canVoidOrders", checked)}
                      />
                      <ToggleSwitch
                        label="Refunds"
                        checked={form.canRefund}
                        disabled={isView}
                        onChange={(checked) => updateField("canRefund", checked)}
                      />
                      <ToggleSwitch
                        label="Cash Drawer"
                        checked={form.canOpenCashDrawer}
                        disabled={isView}
                        onChange={(checked) => updateField("canOpenCashDrawer", checked)}
                      />
                      <ToggleSwitch
                        label="Apply Discount"
                        checked={form.canApplyDiscount}
                        disabled={isView}
                        onChange={(checked) => updateField("canApplyDiscount", checked)}
                      />
                    </div>
                  </div>
                </FormCard>
              </div>
            )}

            {/* Bank Details Tab */}
            {activeTab === "bank" && (
              <FormCard
                icon={CreditCard}
                title="Bank Account Details"
                subtitle="Bank information for salary payments"
              >
                {isAdd ? (
                  <div className="py-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CreditCard size={28} className="text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Bank details coming up</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                      Bank account information can be added after creating the employee profile.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <FormInput
                        icon={Building2} label="Bank Name" disabled={isView}
                        value={form.bankDetails?.[0]?.bankName || ""}
                        onChange={(e) => updateBankField("bankName", e.target.value)}
                        placeholder="e.g., State Bank of India"
                      />
                      <FormInput
                        icon={Hash} label="Account Number" disabled={isView}
                        value={form.bankDetails?.[0]?.accountNumber || ""}
                        onChange={(e) => updateBankField("accountNumber", e.target.value)}
                        placeholder="Account number"
                      />
                      <FormInput
                        icon={User} label="Account Holder Name" disabled={isView}
                        value={form.bankDetails?.[0]?.accountHolderName || ""}
                        onChange={(e) => updateBankField("accountHolderName", e.target.value)}
                        placeholder="Name as per bank"
                      />
                      <FormInput
                        label="IFSC Code" disabled={isView}
                        value={form.bankDetails?.[0]?.ifscCode || ""}
                        onChange={(e) => updateBankField("ifscCode", e.target.value)}
                        placeholder="e.g., SBIN0001234"
                      />
                      <FormInput
                        label="Branch Name" disabled={isView}
                        value={form.bankDetails?.[0]?.branchName || ""}
                        onChange={(e) => updateBankField("branchName", e.target.value)}
                        placeholder="Branch name"
                      />
                      <FormSelect
                        label="Account Type" disabled={isView}
                        value={form.bankDetails?.[0]?.accountType || ""}
                        onChange={(e) => updateBankField("accountType", e.target.value)}
                        options={[
                          { label: "Select type", value: "" },
                          { label: "Savings", value: "SAVINGS" },
                          { label: "Current", value: "CURRENT" },
                          { label: "Salary", value: "SALARY" },
                        ]}
                      />
                    </div>
                    <div className="pt-4 border-t border-slate-100">
                      <ToggleSwitch
                        label="Primary Account"
                        checked={form.bankDetails?.[0]?.isPrimary ?? true}
                        disabled={isView}
                        onChange={(checked) => updateBankField("isPrimary", checked)}
                      />
                    </div>
                  </div>
                )}
              </FormCard>
            )}

            {/* Documents Tab - Hidden for now */}
            {/* {activeTab === "documents" && (...)} */}

            {/* Payroll Tab */}
            {activeTab === "payroll" && (
              <div className="space-y-6">
                {isAdd ? (
                  <FormCard
                    icon={Wallet}
                    title="Payroll Information"
                    subtitle="Salary and compensation details"
                  >
                    <div className="py-12 text-center">
                      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Wallet size={28} className="text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Payroll setup pending</h3>
                      <p className="text-slate-500 max-w-md mx-auto">
                        Payroll information can be configured after creating the employee profile.
                      </p>
                    </div>
                  </FormCard>
                ) : (
                  (() => {
                    // Calculate live salary summary
                    const basic = parseFloat(form.payrollInfo?.basicSalary) || 0;
                    const hra = parseFloat(form.payrollInfo?.hraAllowance) || 0;
                    const transport = parseFloat(form.payrollInfo?.transportAllowance) || 0;
                    const meal = parseFloat(form.payrollInfo?.mealAllowance) || 0;
                    const other = parseFloat(form.payrollInfo?.otherAllowances) || 0;

                    const pfEmployee = parseFloat(form.payrollInfo?.pfContribution) || 0;
                    const esiEmployee = parseFloat(form.payrollInfo?.esiContribution) || 0;
                    const profTax = parseFloat(form.payrollInfo?.professionalTax) || 0;
                    const tdsPercent = parseFloat(form.payrollInfo?.tdsPercent) || 0;

                    const totalEarnings = basic + hra + transport + meal + other;
                    const tdsAmount = (totalEarnings * tdsPercent) / 100;
                    const totalDeductions = pfEmployee + esiEmployee + profTax + tdsAmount;
                    const monthlyGross = totalEarnings;
                    const monthlyNet = totalEarnings - totalDeductions;

                    // PF: 12% of Basic, max ₹1,800 (ceiling ₹15,000)
                    const PF_CEILING = 15000;
                    const pfBase = Math.min(basic, PF_CEILING);
                    const pfEmployer = pfBase * 0.12;

                    // ESI: 3.25% employer if gross <= ₹21,000
                    const esiEmployer = monthlyGross <= 21000 ? monthlyGross * 0.0325 : 0;

                    // Gratuity: configurable or auto-calculate (4.81% of Basic)
                    const gratuity = parseFloat(form.payrollInfo?.gratuityAmount) || (basic * 15 / 26 / 12);

                    const totalEmployerCost = pfEmployer + esiEmployer + gratuity;
                    const annualCTC = (monthlyGross + totalEmployerCost) * 12;
                    const monthlyCTC = monthlyGross + totalEmployerCost;

                    const formatCurrency = (amount) => {
                      if (!amount || isNaN(amount)) return "₹0";
                      return new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(amount);
                    };

                    return (
                      <>
                        {/* Live Salary Summary Preview - Clean Tabular */}
                        {basic > 0 && (
                          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                              <span className="font-semibold text-slate-700 text-sm">Salary Preview</span>
                              <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded">Live Calculation</span>
                            </div>
                            <div className="grid grid-cols-4 divide-x divide-slate-200 text-center text-sm">
                              <div className="p-3">
                                <p className="text-xs text-slate-500">Annual CTC</p>
                                <p className="font-bold text-slate-800">{formatCurrency(annualCTC)}</p>
                              </div>
                              <div className="p-3">
                                <p className="text-xs text-slate-500">Monthly CTC</p>
                                <p className="font-bold text-slate-800">{formatCurrency(monthlyCTC)}</p>
                              </div>
                              <div className="p-3">
                                <p className="text-xs text-slate-500">Gross</p>
                                <p className="font-bold text-slate-800">{formatCurrency(monthlyGross)}</p>
                              </div>
                              <div className="p-3 bg-teal-50">
                                <p className="text-xs text-teal-600">Take-Home</p>
                                <p className="font-bold text-teal-700">{formatCurrency(monthlyNet)}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 divide-x divide-slate-200 text-center text-xs border-t border-slate-100 bg-slate-50/50">
                              <div className="p-2">
                                <span className="text-slate-500">Earnings: </span>
                                <span className="text-emerald-600 font-medium">{formatCurrency(totalEarnings)}</span>
                              </div>
                              <div className="p-2">
                                <span className="text-slate-500">Deductions: </span>
                                <span className="text-rose-600 font-medium">-{formatCurrency(totalDeductions)}</span>
                              </div>
                              <div className="p-2">
                                <span className="text-slate-500">Emp. PF: </span>
                                <span className="text-blue-600 font-medium">{formatCurrency(pfEmployer)}</span>
                              </div>
                              <div className="p-2">
                                <span className="text-slate-500">Gratuity: </span>
                                <span className="text-blue-600 font-medium">{formatCurrency(gratuity)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <FormCard
                          icon={IndianRupee}
                          title="Earnings"
                          subtitle="Basic salary and allowances"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                            <FormInput
                              icon={IndianRupee} label="Basic Salary *" type="number" disabled={isView}
                              value={form.payrollInfo?.basicSalary || ""}
                              onChange={(e) => updatePayrollField("basicSalary", e.target.value)}
                              placeholder="Monthly basic"
                            />
                            <FormInput
                              icon={IndianRupee} label="HRA" type="number" disabled={isView}
                              value={form.payrollInfo?.hraAllowance || ""}
                              onChange={(e) => updatePayrollField("hraAllowance", e.target.value)}
                              placeholder="House Rent Allowance"
                            />
                            <FormInput
                              icon={IndianRupee} label="Transport Allowance" type="number" disabled={isView}
                              value={form.payrollInfo?.transportAllowance || ""}
                              onChange={(e) => updatePayrollField("transportAllowance", e.target.value)}
                              placeholder="Transport"
                            />
                            <FormInput
                              icon={IndianRupee} label="Meal Allowance" type="number" disabled={isView}
                              value={form.payrollInfo?.mealAllowance || ""}
                              onChange={(e) => updatePayrollField("mealAllowance", e.target.value)}
                              placeholder="Meal"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5 pt-5 border-t border-slate-100">
                            <FormInput
                              icon={IndianRupee} label="Other Allowances" type="number" disabled={isView}
                              value={form.payrollInfo?.otherAllowances || ""}
                              onChange={(e) => updatePayrollField("otherAllowances", e.target.value)}
                              placeholder="Other"
                            />
                            <FormSelect
                              icon={Wallet} label="Salary Type" disabled={isView}
                              value={form.payrollInfo?.salaryType || "MONTHLY"}
                              onChange={(e) => updatePayrollField("salaryType", e.target.value)}
                              options={refData?.salaryTypes || [
                                { label: "Monthly", value: "MONTHLY" },
                                { label: "Daily", value: "DAILY" },
                                { label: "Hourly", value: "HOURLY" },
                              ]}
                            />
                            <FormInput
                              icon={Calendar} label="Effective From" type="date" disabled={isView}
                              value={form.payrollInfo?.effectiveFrom || ""}
                              onChange={(e) => updatePayrollField("effectiveFrom", e.target.value)}
                            />
                          </div>
                        </FormCard>

                        <FormCard
                          icon={Percent}
                          title="Deductions & Contributions"
                          subtitle="Statutory deductions (employee share)"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                            <FormInput
                              icon={IndianRupee} label="PF - Employee (12%)" type="number" disabled={isView}
                              value={form.payrollInfo?.pfContribution || ""}
                              onChange={(e) => updatePayrollField("pfContribution", e.target.value)}
                              placeholder="Max ₹1,800"
                            />
                            <FormInput
                              icon={IndianRupee} label="ESI - Employee (0.75%)" type="number" disabled={isView}
                              value={form.payrollInfo?.esiContribution || ""}
                              onChange={(e) => updatePayrollField("esiContribution", e.target.value)}
                              placeholder="If gross ≤ ₹21,000"
                            />
                            <FormInput
                              icon={IndianRupee} label="Professional Tax" type="number" disabled={isView}
                              value={form.payrollInfo?.professionalTax || ""}
                              onChange={(e) => updatePayrollField("professionalTax", e.target.value)}
                              placeholder="State tax"
                            />
                            <FormInput
                              icon={Percent} label="TDS %" type="number" disabled={isView}
                              value={form.payrollInfo?.tdsPercent || ""}
                              onChange={(e) => updatePayrollField("tdsPercent", e.target.value)}
                              placeholder="Tax %"
                            />
                          </div>
                          <div className="mt-5 pt-5 border-t border-slate-100">
                            <p className="text-xs text-slate-500 mb-3">Employer Contributions (Part of CTC)</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                              <div className="p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs text-slate-500">PF - Employer (12%)</p>
                                <p className="text-sm font-semibold text-slate-700">
                                  {formatCurrency(pfEmployer)} <span className="text-xs font-normal text-slate-400">/month</span>
                                </p>
                              </div>
                              <div className="p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs text-slate-500">ESI - Employer (3.25%)</p>
                                <p className="text-sm font-semibold text-slate-700">
                                  {formatCurrency(esiEmployer)} <span className="text-xs font-normal text-slate-400">/month</span>
                                </p>
                              </div>
                              <FormInput
                                icon={IndianRupee} label="Gratuity (monthly)" type="number" disabled={isView}
                                value={form.payrollInfo?.gratuityAmount || ""}
                                onChange={(e) => updatePayrollField("gratuityAmount", e.target.value)}
                                placeholder={`Auto: ₹${Math.round(basic * 15 / 26 / 12)}`}
                              />
                            </div>
                          </div>
                        </FormCard>

                        <FormCard
                          icon={Hash}
                          title="Statutory Information"
                          subtitle="Government compliance numbers"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <FormInput
                              label="PF Account Number" disabled={isView}
                              value={form.payrollInfo?.pfAccountNumber || ""}
                              onChange={(e) => updatePayrollField("pfAccountNumber", e.target.value)}
                              placeholder="PF account number"
                            />
                            <FormInput
                              label="ESI Number" disabled={isView}
                              value={form.payrollInfo?.esiNumber || ""}
                              onChange={(e) => updatePayrollField("esiNumber", e.target.value)}
                              placeholder="ESI number"
                            />
                            <FormInput
                              label="UAN Number" disabled={isView}
                              value={form.payrollInfo?.uanNumber || ""}
                              onChange={(e) => updatePayrollField("uanNumber", e.target.value)}
                              placeholder="Universal Account Number"
                            />
                          </div>
                        </FormCard>

                        <FormCard
                          icon={Clock}
                          title="Rate Settings"
                          subtitle="Hourly, daily rates and overtime"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <FormInput
                              icon={IndianRupee} label="Hourly Rate" type="number" disabled={isView}
                              value={form.payrollInfo?.hourlyRate || ""}
                              onChange={(e) => updatePayrollField("hourlyRate", e.target.value)}
                              placeholder="Per hour"
                            />
                            <FormInput
                              icon={IndianRupee} label="Daily Rate" type="number" disabled={isView}
                              value={form.payrollInfo?.dailyRate || ""}
                              onChange={(e) => updatePayrollField("dailyRate", e.target.value)}
                              placeholder="Per day"
                            />
                            <FormSelect
                              label="Pay Frequency" disabled={isView}
                              value={form.payrollInfo?.payFrequency || "MONTHLY"}
                              onChange={(e) => updatePayrollField("payFrequency", e.target.value)}
                              options={refData?.payFrequencies || [
                                { label: "Monthly", value: "MONTHLY" },
                                { label: "Bi-Weekly", value: "BI_WEEKLY" },
                                { label: "Weekly", value: "WEEKLY" },
                              ]}
                            />
                          </div>
                          <div className="flex flex-col md:flex-row md:items-end gap-5 mt-5 pt-5 border-t border-slate-100">
                            <div className="flex-1">
                              <ToggleSwitch
                                label="Eligible for Overtime Pay"
                                checked={form.payrollInfo?.isOvertimeEligible ?? false}
                                disabled={isView}
                                onChange={(checked) => updatePayrollField("isOvertimeEligible", checked)}
                              />
                            </div>
                            {form.payrollInfo?.isOvertimeEligible && (
                              <div className="flex-1">
                                <FormInput
                                  icon={IndianRupee} label="Overtime Rate (per hour)" type="number" disabled={isView}
                                  value={form.payrollInfo?.overtimeRate || ""}
                                  onChange={(e) => updatePayrollField("overtimeRate", e.target.value)}
                                  placeholder="Rate per hour"
                                />
                              </div>
                            )}
                          </div>
                        </FormCard>
                      </>
                    );
                  })()
                )}
              </div>
            )}

            {/* Shift & Station Tab */}
            {activeTab === "shift" && (
              <div className="space-y-6">
                <FormCard
                  icon={Clock}
                  title="Shift Preferences"
                  subtitle="Preferred working hours and availability"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <FormSelect
                      icon={Clock} label="Preferred Shift" disabled={isView}
                      value={form.shiftPreferences?.preferredShift || ""}
                      onChange={(e) => updateShiftField("preferredShift", e.target.value)}
                      options={refData?.shiftTypes || [
                        { label: "Select shift", value: "" },
                        { label: "Morning Shift", value: "MORNING" },
                        { label: "Afternoon Shift", value: "AFTERNOON" },
                        { label: "Evening Shift", value: "EVENING" },
                        { label: "Night Shift", value: "NIGHT" },
                        { label: "Rotational", value: "ROTATIONAL" },
                      ]}
                    />
                    <FormInput
                      icon={Clock} label="Max Hours/Day" type="number" disabled={isView}
                      value={form.shiftPreferences?.maxHoursPerDay || ""}
                      onChange={(e) => updateShiftField("maxHoursPerDay", e.target.value)}
                      placeholder="e.g., 8"
                    />
                    <FormInput
                      icon={Clock} label="Max Hours/Week" type="number" disabled={isView}
                      value={form.shiftPreferences?.maxHoursPerWeek || ""}
                      onChange={(e) => updateShiftField("maxHoursPerWeek", e.target.value)}
                      placeholder="e.g., 48"
                    />
                  </div>

                  <div className="mt-6 pt-5 border-t border-slate-100">
                    <label className="block text-sm font-medium text-slate-700 mb-4">Weekly Availability</label>
                    <div className="flex flex-wrap gap-2">
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day, idx) => {
                        const isAvailable = form.shiftPreferences?.[`available${day}`] ?? (day !== "Sunday");
                        const isWeekend = idx >= 5;
                        return (
                          <button
                            key={day}
                            type="button"
                            disabled={isView}
                            onClick={() => updateShiftField(`available${day}`, !isAvailable)}
                            className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center transition-all duration-200 ${isAvailable
                              ? isWeekend
                                ? "bg-amber-50 border border-amber-200 hover:bg-amber-100"
                                : "bg-emerald-50 border border-emerald-200 hover:bg-emerald-100"
                              : "bg-slate-50 border border-slate-200 hover:bg-slate-100"
                              } ${isView ? "cursor-not-allowed opacity-60" : ""}`}
                          >
                            <span className={`text-[10px] font-semibold uppercase ${isAvailable
                              ? isWeekend ? "text-amber-600" : "text-emerald-600"
                              : "text-slate-400"
                              }`}>
                              {day.slice(0, 3)}
                            </span>
                            <span className={`text-lg ${isAvailable
                              ? isWeekend ? "text-amber-500" : "text-emerald-500"
                              : "text-slate-300"
                              }`}>
                              {isAvailable ? "✓" : "−"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </FormCard>

                <FormCard
                  icon={Building2}
                  title="Assigned Stations"
                  subtitle="Kitchen stations this employee works at"
                >
                  {isAdd ? (
                    <div className="py-12 text-center">
                      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Building2 size={28} className="text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Station assignment pending</h3>
                      <p className="text-slate-500 max-w-md mx-auto">
                        Station assignments can be configured after creating the employee profile.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {form.assignedStations?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {form.assignedStations.map((station, idx) => (
                            <div
                              key={station.id || idx}
                              className="flex items-center justify-between p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-teal-100 text-teal-600 rounded-xl flex items-center justify-center">
                                  <Building2 size={18} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">{station.kitchenStationName || "Unknown Station"}</p>
                                  {station.isPrimary && (
                                    <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                                      <CheckCircle2 size={12} /> Primary
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-8 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                          <p className="text-slate-500">No stations assigned yet</p>
                        </div>
                      )}

                      {!isView && (
                        <div className="pt-4 border-t border-slate-100">
                          <label className="block text-sm font-medium text-slate-700 mb-3">Add Station</label>
                          {refData?.kitchenStations?.length > 0 ? (
                            <div className="flex gap-3">
                              <select
                                value={selectedStationId}
                                onChange={(e) => setSelectedStationId(e.target.value)}
                                disabled={addingStation}
                                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none disabled:bg-slate-100 bg-white"
                              >
                                <option value="">Select a station...</option>
                                {refData.kitchenStations
                                  .filter(ks => !form.assignedStations?.some(as => as.kitchenStationId === ks.id))
                                  .map(ks => (
                                    <option key={ks.id} value={ks.id}>{ks.name}</option>
                                  ))}
                              </select>
                              <button
                                type="button"
                                disabled={!selectedStationId || addingStation}
                                onClick={async () => {
                                  if (!selectedStationId || !id) return;
                                  setAddingStation(true);
                                  try {
                                    const assigned = await employeeService.assignStation(id, {
                                      kitchenStationId: parseInt(selectedStationId),
                                      isPrimary: form.assignedStations?.length === 0
                                    });
                                    setForm(prev => ({
                                      ...prev,
                                      assignedStations: [...(prev.assignedStations || []), assigned]
                                    }));
                                    setSelectedStationId("");
                                  } catch (err) {
                                    console.error("Failed to assign station:", err);
                                    alert("Failed to assign station. Please try again.");
                                  } finally {
                                    setAddingStation(false);
                                  }
                                }}
                                className="px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200"
                              >
                                {addingStation ? (
                                  <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Adding...
                                  </>
                                ) : (
                                  <>
                                    <Plus size={16} />
                                    Add
                                  </>
                                )}
                              </button>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 bg-slate-50 p-4 rounded-xl">
                              No kitchen stations available. Please create stations first.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </FormCard>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

/* Form Card Component */
function FormCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
}

/* Form Input Component */
function FormInput({ icon: Icon, label, error, required, disabled = false, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={16} />
          </div>
        )}
        <input
          {...props}
          disabled={disabled}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 border rounded-xl text-sm transition-all duration-200 outline-none ${error
            ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
            : "border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"}`}
        />
      </div>
      {error && <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} /> {error}</p>}
    </div>
  );
}

/* Form Select Component */
function FormSelect({ icon: Icon, label, error, required, disabled = false, options = [], ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            <Icon size={16} />
          </div>
        )}
        <select
          {...props}
          disabled={disabled}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 border rounded-xl text-sm transition-all duration-200 outline-none appearance-none ${error
            ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
            : "border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"}`}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      {error && <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} /> {error}</p>}
    </div>
  );
}

/* Toggle Switch Component */
function ToggleSwitch({ label, checked, disabled = false, onChange }) {
  return (
    <label className={`flex items-center gap-3 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${checked ? "bg-teal-600" : "bg-slate-200"
          } ${disabled ? "opacity-50" : ""}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0"
            }`}
        />
      </button>
      <span className={`text-sm font-medium ${disabled ? "text-slate-400" : "text-slate-700"}`}>
        {label}
      </span>
    </label>
  );
}
